/**
 * Leitor de codigo de barras / QR — camera nativa via ML Kit.
 *
 * Dois usos no app:
 *   - codigo de barras de produto (EAN/UPC/Code-128) na entrada e saida de estoque
 *   - QR da NFC-e, que alimenta backend/nfce_client.py
 *
 * Na web nao ha implementacao: disponivel() devolve false e a UI simplesmente
 * nao mostra o botao de camera. Digitar o codigo continua funcionando nos dois
 * lados — o scanner e um atalho, nunca o unico caminho.
 */

import { isNative, getPlugin } from './native';

const LIMITE_SEQUENCIA = 60;   // trava de seguranca contra loop infinito

// VALORES do enum BarcodeFormat do plugin, nao as chaves. Estavam como
// 'Ean13'/'QrCode' — nomes que o plugin nao reconhece, entao o filtro nunca
// filtrou nada e a camera lia qualquer formato. Passou despercebido porque
// "sem filtro" funciona; o que nao funcionava era o modo so-QR ser so-QR.
const FORMATOS_PRODUTO = ['EAN_13', 'EAN_8', 'UPC_A', 'UPC_E', 'CODE_128', 'CODE_39', 'ITF'];
const FORMATOS_QR = ['QR_CODE'];

function plugin() {
  return getPlugin('BarcodeScanner');
}

/** true se da para escanear nesta plataforma. */
export function disponivel() {
  return isNative() && !!plugin();
}

/**
 * Garante permissao de camera. Retorna true se pode escanear.
 * A recusa nao lanca excecao: quem chama decide o que dizer para a usuaria.
 */
async function garantirPermissao(p) {
  try {
    const { camera } = await p.checkPermissions();
    if (camera === 'granted' || camera === 'limited') return true;
    const pedido = await p.requestPermissions();
    return pedido.camera === 'granted' || pedido.camera === 'limited';
  } catch {
    return false;
  }
}

/**
 * No Android o ML Kit vem como modulo separado, baixado sob demanda pelo
 * Google Play Services. Sem isso o primeiro scan falha em aparelho novo.
 * Idempotente: se ja estiver instalado, retorna na hora.
 */
async function garantirModuloAndroid(p) {
  if (typeof p.isGoogleBarcodeScannerModuleAvailable !== 'function') return true;
  try {
    const { available } = await p.isGoogleBarcodeScannerModuleAvailable();
    if (available) return true;
    await p.installGoogleBarcodeScannerModule();
    return true;
  } catch {
    // Sem Play Services (aparelho chines, emulador sem GMS): segue e deixa o
    // scan falhar com mensagem propria em vez de travar aqui.
    return false;
  }
}

/**
 * Abre a camera e devolve o conteudo lido.
 *
 * @param {{ tipo?: 'produto'|'qr'|'ambos' }} opcoes
 * @returns {Promise<{ ok: boolean, valor?: string, formato?: string, motivo?: string }>}
 *
 * `ambos` existe para o leitor unico da topbar: a pessoa aponta a camera sem
 * dizer antes se e etiqueta ou embalagem. O `formato` volta junto porque e ele
 * que desfaz o empate — um EAN-8 sem 0 e sem 1 casa com o formato do codigo de
 * etiqueta, e o texto sozinho nao distingue os dois.
 *
 * motivo: 'indisponivel' | 'sem-permissao' | 'cancelado' | 'erro'
 * Cancelar nao e erro — a usuaria fechou a camera de proposito.
 */
export async function escanear({ tipo = 'produto' } = {}) {
  const p = plugin();
  if (!p) return { ok: false, motivo: 'indisponivel' };

  if (!(await garantirPermissao(p))) return { ok: false, motivo: 'sem-permissao' };
  await garantirModuloAndroid(p);

  try {
    let formats = FORMATOS_PRODUTO;
    if (tipo === 'qr') formats = FORMATOS_QR;
    else if (tipo === 'ambos') formats = [...FORMATOS_QR, ...FORMATOS_PRODUTO];

    const { barcodes } = await p.scan({ formats });
    const lido = barcodes && barcodes.length ? barcodes[0] : null;
    const valor = lido ? (lido.rawValue || '').trim() : '';
    if (!valor) return { ok: false, motivo: 'cancelado' };
    return { ok: true, valor, formato: lido.format };
  } catch (e) {
    // O plugin lanca ao cancelar em algumas versoes; tratamos como cancelamento.
    const msg = (e && e.message ? e.message : '').toLowerCase();
    if (msg.includes('cancel')) return { ok: false, motivo: 'cancelado' };
    return { ok: false, motivo: 'erro' };
  }
}

/**
 * Le varios codigos em sequencia, reabrindo a camera apos cada leitura.
 *
 * Usa o leitor em tela cheia do Google em loop, e nao o startScan continuo:
 * o continuo exige deixar o webview transparente e desenhar a UI da camera por
 * conta, o que e bem mais frágil. O custo e um flash entre leituras.
 *
 * Para a cada leitura: haptic + callback. A usuaria encerra fechando a camera.
 *
 * @param {(codigo: string, indice: number) => void} aoLer
 * @returns {Promise<{ codigos: string[], motivo: string }>}
 *   motivo: 'cancelado' (fim normal) | 'indisponivel' | 'sem-permissao' | 'erro' | 'limite'
 */
export async function escanearVarios(aoLer) {
  if (!disponivel()) return { codigos: [], motivo: 'indisponivel' };

  const Haptics = getPlugin('Haptics');
  const codigos = [];

  for (let i = 0; i < LIMITE_SEQUENCIA; i += 1) {
    const r = await escanear({ tipo: 'produto' });
    if (!r.ok) {
      // 'cancelado' aqui e o fim normal da sequencia, nao erro.
      return { codigos, motivo: r.motivo };
    }
    codigos.push(r.valor);
    // Confirmacao tatil: a usuaria esta olhando a embalagem, nao a tela.
    if (Haptics) Promise.resolve(Haptics.impact({ style: 'MEDIUM' })).catch(() => {});
    try { aoLer(r.valor, codigos.length); } catch { /* callback nao derruba a leitura */ }
  }
  return { codigos, motivo: 'limite' };
}

/**
 * Casa um codigo lido com a lista de produtos ja carregada.
 *
 * Busca IGUALDADE, nao substring: para digitacao o `includes` do formulario
 * faz sentido, mas para um codigo escaneado inteiro ele e perigoso — um EAN
 * pode ser substring de outro e selecionaria o produto errado.
 * Considera tambem o codigo alternativo (caixa x unidade).
 */
export function casarPorCodigo(produtos, codigo) {
  const alvo = (codigo || '').trim();
  if (!alvo) return [];
  return (produtos || []).filter(p =>
    (p.codigo_barras || '').trim() === alvo ||
    (p.codigo_barras_alternativo || '').trim() === alvo
  );
}
